import React, { ReactNode, FC, useContext, useMemo, useRef, useState, ComponentType } from 'react';
import { ScreenClassContext } from 'react-grid-system';
import { ThemeContext } from 'styled-components';

import useResize from '../../../hooks/_useResize';
import MainContentContext from '../_MainContentContext';
import ColorSchemeContext from '../../colorScheme/ColorSchemeContext';

import { StyledContent, StyledContentContainer } from './MainContent.styles';

export interface MainContentProps {
  children?: ReactNode;
  /** Removes the padding */
  nopadding?: boolean;
  /** Horizontal padding */
  horizontalPadding?: number;
  /** Vertical padding */
  verticalPadding?: number;
  /** 'background' style of the content */
  background?: string;
  /** 'background-repeat' style of the content */
  backgroundRepeat?: string;
  /** 'background-size' style of the content */
  backgroundSize?: string;
  /** 'background-position' style of the content */
  backgroundPosition?: string;
  /** 'background-attachment' style of the content */
  backgroundAttachment?: string;
  /** Height css of the content */
  contentHeight?: string;
  /** Minimum width of the content. */
  minContentWidth?: number;
  /** Maximum width of the content. */
  maxContentWidth?: number;
  /** App name. */
  appName?: string;
  /** Stretch the content to fill the avaiable height. */
  fullHeight?: boolean;
  /** Title type to used in PrimaryPage. */
  PrimaryTitle?: ComponentType<{ children: ReactNode }>;
  /** Title type to used in SecondaryPage. */
  SecondaryTitle?: ComponentType<{ children: ReactNode }>;
}

const MainContentDefaultProps = {
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
};

/**
 * @since 0.14.0
 */
const MainContent: FC<MainContentProps> = ({
  appName, children, nopadding, horizontalPadding, verticalPadding, minContentWidth, maxContentWidth, contentHeight, PrimaryTitle, SecondaryTitle, fullHeight, ...props
}) => {
  const containerRef = useRef<HTMLDivElement>();
  const contentRef = useRef();
  const [contentWidth, setContentWidth] = useState(0);
  const theme = useContext(ThemeContext);
  const { scheme } = useContext(ColorSchemeContext);

  const upperBreakpoint = useContext(ScreenClassContext);
  const isSmall = useMemo(() => ['xs', 'sm', 'md'].includes(upperBreakpoint), [upperBreakpoint]);

  const paddingTop = useMemo(() => {
    if (nopadding) return 0;
    if (isSmall) return verticalPadding || theme.layout.contentVerticalPadding || 48;
    return verticalPadding || theme.layout.lgContentVerticalPadding || 60;
  }, [isSmall, nopadding, theme.layout.contentVerticalPadding, theme.layout.lgContentVerticalPadding, verticalPadding]);

  const mainContentContext = useMemo(() => ({
    appName,
    PrimaryTitle,
    SecondaryTitle,
    paddingTop,
    contentWidth,
  }), [PrimaryTitle, SecondaryTitle, appName, contentWidth, paddingTop]);

  useResize(containerRef, () => {
    setContentWidth(containerRef.current?.clientWidth);
  }, { delay: 10 });

  return (
    <StyledContent
      scheme={scheme}
      data-testid="MainContent"
      {...props}
      ref={containerRef}
      fullHeight={fullHeight}
    >
      <StyledContentContainer
        nopadding={nopadding}
        horizontalPadding={horizontalPadding}
        verticalPadding={verticalPadding}
        minContentWidth={minContentWidth}
        maxContentWidth={maxContentWidth}
        contentHeight={contentHeight}
        fullHeight={fullHeight}
        ref={contentRef}
      >
        <MainContentContext.Provider value={mainContentContext}>
          {children}
        </MainContentContext.Provider>
      </StyledContentContainer>
    </StyledContent>
  );
};

MainContent.defaultProps = MainContentDefaultProps;

export default MainContent;
